import Icon from '@/components/ui/AppIcon';

interface StatusAlert {
  id: string;
  applicationId: string;
  type: 'status_update' | 'document_request' | 'approval' | 'delay' | 'urgent_action';
  title: string;
  message: string;
  timestamp: string;
  priority: 'high' | 'medium' | 'low';
  read: boolean;
}

interface StatusAlertsProps {
  alerts: StatusAlert[];
  onMarkAsRead: (alertId: string) => void;
}

export default function StatusAlerts({ alerts, onMarkAsRead }: StatusAlertsProps) {
  const alertConfig = {
    status_update: { icon: 'InformationCircleIcon', color: 'text-sky-blue', bgColor: 'bg-sky-blue/10' },
    document_request: { icon: 'DocumentTextIcon', color: 'text-warning', bgColor: 'bg-warning/10' },
    approval: { icon: 'CheckCircleIcon', color: 'text-trust-green', bgColor: 'bg-trust-green/10' },
    delay: { icon: 'ClockIcon', color: 'text-coral', bgColor: 'bg-coral/10' },
    urgent_action: { icon: 'ExclamationTriangleIcon', color: 'text-error', bgColor: 'bg-error/10' }
  };

  const priorityConfig = {
    high: 'border-coral/30 bg-coral/5',
    medium: 'border-warning/30 bg-warning/5',
    low: 'border-border bg-white'
  };

  const unreadAlerts = alerts.filter(a => !a.read);

  if (alerts.length === 0) {
    return null;
  }

  return (
    <div className="bg-white rounded-xl shadow-brand p-6 border-2 border-border">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-coral/10 flex items-center justify-center">
            <Icon name="BellIcon" size={20} className="text-coral" />
          </div>
          <div>
            <h3 className="font-heading font-bold text-lg text-foreground">
              Status Alerts
            </h3>
            <p className="text-xs text-muted-foreground">
              {unreadAlerts.length} unread notification{unreadAlerts.length !== 1 ? 's' : ''}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 bg-trust-green rounded-full animate-pulse" />
          <span className="text-xs text-muted-foreground font-medium">Real-time updates</span>
        </div>
      </div>

      <div className="space-y-3 max-h-96 overflow-y-auto">
        {alerts.map((alert) => {
          const config = alertConfig[alert.type];
          return (
            <div
              key={alert.id}
              className={`relative p-4 rounded-lg border-2 transition-all duration-200 hover:shadow-md ${
                priorityConfig[alert.priority]
              } ${!alert.read ? 'ring-2 ring-primary/20' : 'opacity-75'}`}
            >
              {!alert.read && (
                <div className="absolute top-2 right-2 w-2 h-2 bg-primary rounded-full animate-pulse" />
              )}
              
              <div className="flex items-start gap-3">
                <div className={`w-10 h-10 rounded-full ${config.bgColor} flex items-center justify-center flex-shrink-0`}>
                  <Icon name={config.icon as any} size={20} className={config.color} />
                </div>
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between mb-1">
                    <h4 className="font-heading font-semibold text-foreground">{alert.title}</h4>
                    {alert.priority === 'high' && (
                      <span className="px-2 py-0.5 bg-coral text-white text-xs font-bold rounded ml-2">
                        HIGH
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground mb-2">{alert.message}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">{alert.timestamp}</span>
                    {!alert.read && (
                      <button
                        onClick={() => onMarkAsRead(alert.id)}
                        className="text-xs font-medium text-primary hover:text-primary/80 transition-colors"
                      >
                        Mark as read
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}